import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { publicApi, apiError } from "../../lib/api";
import { Page, H1, Lead, P } from "./primitives";

const SUBJECTS = ["general", "business", "enterprise", "technical", "dpa", "vulnerability", "partnership", "other"];

// Pré-remplissage via ?subject= (utilisé par les pages DPA et Signalement de vulnérabilité).
const SUBJECT_ALIASES = { dpa: "dpa", security: "vulnerability", vulnerabilite: "vulnerability", business: "business", enterprise: "enterprise" };

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
    {children}
  </div>
);

export const ContactPage = () => {
  const { t, i18n } = useTranslation("public");
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", subject: "general", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const raw = (params.get("subject") || "").toLowerCase().trim();
    const mapped = SUBJECT_ALIASES[raw] || (SUBJECTS.includes(raw) ? raw : null);
    if (mapped) setForm((f) => ({ ...f, subject: mapped }));
  }, [params]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error(t("contactPage.errName")); return; }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error(t("contactPage.errEmail")); return; }
    if (!form.message.trim()) { toast.error(t("contactPage.errMessage")); return; }
    setSending(true);
    try {
      await publicApi.post("/contact", {
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: t(`contactPage.subjects.${form.subject}`),
        message: form.message.trim(),
        locale: i18n.language,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Page testid="contact-page">
      <H1>{t("contactPage.title")}</H1>
      <Lead>{t("contactPage.lead")}</Lead>

      {submitted ? (
        <div data-testid="contact-notice" className="glass rounded-2xl p-6 border border-emerald-300 bg-emerald-50/60">
          <p className="text-sm text-emerald-800">{t("contactPage.sent")}</p>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="contact-name" label={t("contactPage.name")}>
              <Input id="contact-name" data-testid="contact-name" value={form.name} onChange={set("name")} className="mt-1 bg-white border-slate-200" />
            </Field>
            <Field id="contact-company" label={t("contactPage.company")}>
              <Input id="contact-company" data-testid="contact-company" value={form.company} onChange={set("company")} className="mt-1 bg-white border-slate-200" />
            </Field>
            <Field id="contact-email" label={t("contactPage.email")}>
              <Input id="contact-email" data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="mt-1 bg-white border-slate-200" />
            </Field>
            <Field id="contact-phone" label={<>{t("contactPage.phone")} <span className="text-slate-400 font-normal">{t("contactPage.optional")}</span></>}>
              <Input id="contact-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="mt-1 bg-white border-slate-200" />
            </Field>
          </div>
          <Field id="contact-subject" label={t("contactPage.subject")}>
            <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
              <SelectTrigger id="contact-subject" data-testid="contact-subject" className="mt-1 bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUBJECTS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`contact-subject-${s}`}>{t(`contactPage.subjects.${s}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="contact-message" label={t("contactPage.message")}>
            <Textarea id="contact-message" data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} className="mt-1 bg-white border-slate-200" />
            <p className="text-[11px] text-amber-600 mt-1">{t("contactPage.sensitiveWarning")}</p>
          </Field>
          <Button data-testid="contact-submit" onClick={submit} disabled={sending} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            {t("contactPage.submit")}
          </Button>
        </div>
      )}
    </Page>
  );
};

export const AboutPage = () => {
  const { t } = useTranslation("public");
  return (
    <Page testid="about-page">
      <H1>{t("aboutPage.title")}</H1>
      <Lead>{t("aboutPage.lead")}</Lead>
      <P>{t("aboutPage.p1")}</P>
      <P>{t("aboutPage.p2")}</P>
      <P><Trans t={t} i18nKey="aboutPage.p3" components={{ b: <b /> }} /></P>
    </Page>
  );
};

export default ContactPage;
